import { Component } from 'react';
import { connect } from 'react-redux';

import styles from './ExternalUsers.scss';
import icons from 'styles/icons/icons.scss';

import QuickMenu from 'common/components/QuickMenu/QuickMenu';
import FileSearchBar from 'common/components/FileSearchBar/FileSearchBar';
import Part11Indicator from 'common/components/Part11Indicator/Part11Indicator';
import DateTime from 'common/components/DateTime/DateTime';
import ExternalUserSearch from './ExternalUserSearch';
import AddExternalUserModal from './AddExternalUserModal';

import {
    setActionButton,
    clearActionButton,
    buildActionButtonProperty,
    closeModal,
} from 'common/actions/actionButtonActions';
import {
    loadExternalUsers,
    removeExternalUser,
    clearExternalUser,
    sendNotificationEmail
} from 'binder/actions/externalUsers';

import { binderInstitutionIdSelector } from 'common/selectors';
import { DEFAULT_DATETIME_INPUT_FORMAT } from 'common/util/dateTime';

import moment from 'moment';
import lodash from 'lodash';

function QuickActionLabel({ labelText }) {
    return (
        <div className={styles.quickActionLabel}>
            <span>{labelText}</span>
            <Part11Indicator className={styles.part11Icon} />
        </div>
    );
}

class ExternalUserQuickActions extends Component {
    render() {
        const options = [{
            label: 'Edit Access Window',
            handler: () => {
                this.props.editUser(this.props.user);
            },
        }, {
            label: <QuickActionLabel labelText="Send Notification Email" />,
            handler: () => this.props.sendNotificationEmail()
        }, {
            label: <QuickActionLabel labelText="Remove from Binder" />,
            handler: () => this.props.removeUserFromBinder()
        }];

        return (
            <QuickMenu headerText="Actions" theme="primary" options={options} />
        );
    }
}

class ExternalUsersInfo extends Component {
    render() {
        const startDate = lodash.get(this.props, 'user.binderExpiration.startDate');
        const endDate = lodash.get(this.props, 'user.binderExpiration.endDate');

        return (
            <div data-cy="ExternalUsers">
                <div data-cy="ExternalUsers-DisplayName">
                    {this.props.user.displayName}
                </div>
                <div className={styles.container} data-cy="ExternalUsers-FullName">
                    {this.props.user.firstName} {this.props.user.LastName} | {this.props.user.emailAddress}
                </div>
                <div className={styles.container} data-cy="ExternalUsers-Date">
                    Accessible <DateTime value={startDate} inputFormat={DEFAULT_DATETIME_INPUT_FORMAT} time={true} format={'MM-DD-YYYY h:mmA'} />  through <DateTime value={endDate} inputFormat={DEFAULT_DATETIME_INPUT_FORMAT} time={true} format={'MM-DD-YYYY h:mmA'} />
                </div>
            </div>
        );
    }
}

class ExternalUser extends Component {
    constructor(props) {
        super(props);

        this.state = {
            editable: false,
        };

        this.enableEditMode = this.enableEditMode.bind(this);
        this.disableEditMode = this.disableEditMode.bind(this);
    }

    getStatusClass(user) {
        const currentTime = moment();
        const userStartTime = moment(user.binderExpiration.startDate, DEFAULT_DATETIME_INPUT_FORMAT);
        const userEndTime = moment(user.binderExpiration.endDate, DEFAULT_DATETIME_INPUT_FORMAT);
        const isExpired = currentTime.diff(userEndTime) >= 0;
        const isStarted = currentTime.diff(userStartTime) >= 0;

        if (isExpired) {
            return styles.expired;
        }
        if (isStarted) {
            return styles.active;
        }
        return styles.upcoming;
    }

    enableEditMode(user) {
        this.setState({ editable: true });
    }

    disableEditMode() {
        this.setState({ editable: false });
        closeModal('addExternalUserModal');
    }

    render() {
        const statusClass = this.getStatusClass(this.props.user);

        const { user, binderId, institutionId } = this.props;
        const userId = user.id;
        const { startDate, endDate } = user.binderExpiration;

        return (
            <div className={styles.row} data-cy="ExternalUsers-getStatusClass">
                <div className={styles.statusContainer}>
                    <span className={`${icons.materialIcons} ${statusClass}`}>lens</span>
                </div>
                {this.state.editable
                    ? <AddExternalUserModal externalUser={this.props.user} onSubmit={this.disableEditMode} onClose={this.disableEditMode} /> : null}
                <div className={styles.info} >
                    <ExternalUsersInfo user={this.props.user} />
                </div>
                <div className={styles.quickActions} >
                    <ExternalUserQuickActions
                        user={this.props.user}
                        sendNotificationEmail={() => this.props.sendNotificationEmail(userId, binderId, institutionId, startDate, endDate)}
                        removeUserFromBinder={this.props.removeUserFromBinder}
                        editUser={this.enableEditMode} />
                </div>
            </div>
        );
    }
}

class ExternalUsers extends Component {
    constructor(props) {
        super(props);

        this.closeModal = this.closeModal.bind(this);
    }

    loadUsers(binderId, institutionId, query) {
        query = query || {};

        const searchOptions = {
            queryText: query.q,
            sortField: query.s,
            sortOrder: query.o,
        };
        this.props.loadExternalUsers(binderId, institutionId, searchOptions);
    }

    componentDidMount() {
        this.props.setActionButton(buildActionButtonProperty('Add External User', 'addExternalUserModal', 'modal'));
        this.loadUsers(this.props.binder.id, this.props.institutionId);

    }

    componentDidUpdate(prevProps) {
        const nextBinder = this.props.binder;
        const currentLocation = prevProps.location;
        const nextLocation = this.props.location;
        const nextInstitutionId = this.props.institutionId;

        const queryChanged = !lodash.isEqual(currentLocation.query, nextLocation.query) && !lodash.isNil(nextBinder);

        if (queryChanged) {
            this.loadUsers(nextBinder.id, nextInstitutionId, nextLocation.query);
        }
    }

    componentWillUnmount() {
        this.props.clearActionButton();
    }

    closeModal() {
        this.props.closeModal('addExternalUserModal');
        this.props.clearExternalUser();
    }

    render() {
        return (
            <div data-cy="ExternalUsers-Modal">
                {this.props.addExternalUserAction && <ExternalUserSearch open={this.props.addExternalUserAction} onClose={this.closeModal} />}
                {this.props.existingExternalUser ? <AddExternalUserModal externalUser={this.props.existingExternalUser} onSubmit={this.closeModal} onClose={this.closeModal} /> : null}
                <FileSearchBar placeholder="Search External Users" />
                <div data-cy="ExternalUsers-map">
                    {this.props.externalUsers.map((user, i) =>
                        <ExternalUser
                            binderId={this.props.binder.id}
                            institutionId={this.props.institutionId}
                            user={user}
                            key={i}
                            sendNotificationEmail={this.props.sendNotificationEmail}
                            removeUserFromBinder={this.props.removeExternalUser.bind(this, user, this.props.binder.id, this.props.institutionId)}
                            loadExternalUsers={() => this.props.loadExternalUsers(this.props.binder.id, this.props.institutionId)}
                        />)}
                </div>
            </div>
        );
    }
}

export default connect((state, props) => {
    const binder = lodash.get(state, 'binder', {});
    const institutionId = binderInstitutionIdSelector(state);
    const existingExternalUser = lodash.get(state, 'existingExternalUser', null);
    const externalUsers = lodash.get(state, 'externalUsers', []);
    const addExternalUserAction = lodash.get(state, 'app.actionButton.addExternalUserModalOpen', false);
    const canManageExternalUserAccess = lodash.get(state, 'me.binderPermissions.manageExternalUserAccess', false);
    return {
        binder,
        institutionId,
        existingExternalUser,
        externalUsers,
        addExternalUserAction,
        canManageExternalUserAccess,
    };
}, {
    loadExternalUsers,
    setActionButton,
    clearActionButton,
    removeExternalUser,
    closeModal,
    clearExternalUser,
    sendNotificationEmail
})(ExternalUsers);
